import React, { useMemo, useRef, useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select, { SingleValue, components } from "react-select";
import CreatableSelect from "react-select/creatable";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { UIInputFF, UIReactSelectFF } from "components/Adapter";
import { showSuccess, showError } from "toast/toast";
import { Building2, Users, Globe } from "lucide-react"
import { useLoadScript } from "@react-google-maps/api";
import { VendorProfile } from "types";
import { uploadCloudFile, getCloudFileById, resolveImageId, hardDeletePresignedFiles } from "components/Imageshelper";
import AddressAutocomplete from "components/AddressAutocomplete";
import { sendTriggerEmails } from "utils/emailService";
import { handleApiError } from "utils/errorHandler";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
import { getAuthCookie } from "../../utils/authCookies";
/** 
 * MarketplaceVendorProfile
 * - Single page for Add + Edit
 * - UI-only (no API calls wired) — slots are marked with TODOs
 */
// --- Payload types (mirror your backend model) ---
type LocationPayload = {
    label?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
    cityCode?: string;
    stateCode?: string;
    countryCode?: string;
    latitude?: number;
    longitude?: number;
    phone?: string;
};

type CreateMarketplaceProfilePayload = {
    user_id: string;
    vendor_name: string;
    logo?: string;
    banner?: string;
    category: string;
    tag_line: string;
    description?: string;
    about_company?: string;

    social_links?: {
        facebook?: string;
        instagram?: string;
        twitter?: string;
        linkedin?: string;
        youtube?: string;
    };

    terms_and_conditions?: {
        text?: string;
        created_at?: string;
        updated_at?: string;
    };

    contact_info?: {
        website?: string;
        email?: string;
        phone?: string;
        location?: LocationPayload;
    };

    business_details?: {
        year_of_established?: number;
        number_of_employees?: number;
        response_time?: number;       // hours
        completed_projects?: number;
        price_model?: string;         // "per_project" | "hourly" | etc.
    };

    core_specialties?: string[];
    certifications_compliance?: string[];
    why_choose_us?: string;

    verified: boolean;
    partner: boolean;
    status: "pending" | "approved" | "active" | "suspended";
    catalog_visibility: "public" | "assigned";
    is_private: boolean;
};

type SocialLinksFormValues = {
    facebook: string;
    instagram: string;
    twitter: string;
    linkedin: string;
    youtube: string;
};

function socialLinksPayloadFromValues(sl?: SocialLinksFormValues) {
    if (!sl) return {};

    const out = {
        facebook: sl.facebook?.trim() || undefined,
        instagram: sl.instagram?.trim() || undefined,
        twitter: sl.twitter?.trim() || undefined,
        linkedin: sl.linkedin?.trim() || undefined,
        youtube: sl.youtube?.trim() || undefined,
    };

    return Object.fromEntries(
        Object.entries(out).filter(([, value]) => Boolean(value))
    );
}

const DropdownIndicator = (props: any) => {
    if (props.selectProps.isDisabled) return null;
    return (
        <components.DropdownIndicator {...props}>
            <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
        </components.DropdownIndicator>
    );
};

const customSelectStyles = {
    /* hide all indicators when disabled */
    indicatorsContainer: (provided: any, state: any) => ({
        ...provided,
        display: state.isDisabled ? "none" : "flex",
    }),
    dropdownIndicator: (provided: any, state: any) => ({
        ...provided,
        display: state.isDisabled ? "none" : "flex",
    }),
    indicatorSeparator: () => ({ display: "none" }),

    /* keep bg and cursor right; do NOT fade */
    control: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: state.isDisabled ? "#f2f2f2" : provided.backgroundColor,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        opacity: 1,
    }),

    /* ——— make text BLACK when disabled ——— */
    singleValue: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
    }),
    valueContainer: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
    }),
    input: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
        WebkitTextFillColor: state.isDisabled ? "#000" : undefined, // Safari
    }),
    placeholder: (provided: any, state: any) => ({
        ...provided,
        /* if field has no value, keep placeholder readable too */
        color: state.isDisabled ? "#000" : provided.color,
    }),

    /* if you ever use isMulti, keep chips readable */
    multiValue: (p: any, s: any) => ({
        ...p,
        backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor,
    }),
    multiValueLabel: (p: any, s: any) => ({
        ...p,
        color: s.isDisabled ? "#000" : p.color,
    }),
    multiValueRemove: (p: any, s: any) => ({
        ...p,
        display: s.isDisabled ? "none" : p.display,
    }),
};

const libraries: Array<"places"> = ["places"];
const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const toUrlString = (res: unknown): string | undefined => {
    if (!res) return undefined;
    if (typeof res === "string") return res;
    const r = res as any;
    const u = r?.url ?? r?.signedUrl ?? r?.data?.url;
    return typeof u === "string" ? u : undefined;
};


const MarketplaceVendorProfile: React.FC = () => {
       const location = useLocation();
        const isSignageTeammember = location.pathname.includes('/signageteammember');
        const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
    const navigate = useNavigate();
    const { vendorId } = useParams<{ vendorId: string }>(); // if present => Edit mode
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const userName = getAuthCookie("userName");
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: apiKey,
        libraries,
    });
    const [profile, setProfile] = React.useState<VendorProfile | null>(null);
    const [isEdit, setIsEdit] = React.useState(false);
    const [editingId, setEditingId] = React.useState<string | null>(null);


    // Images related code start
    const [serverImageSrc, setServerImageSrc] = useState<string | undefined>(undefined);
    const [cloudFileId, setCloudFileId] = useState<string | null>(null);

    const normalizePath = (p: string) => p.replace(/\\/g, "/").replace(/^\/+/, "");
    const buildAbsoluteFromRelative = (p: string) => {
        const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
        return `${base}/${normalizePath(p)}`;
    };
    useEffect(() => {
        let active = true;

        (async () => {
            const val = profile?.logo;
            if (!val) {
                if (active) setServerImageSrc(undefined);
                return;
            }

            if (/^https?:\/\//i.test(val)) {
                if (active) setServerImageSrc(val);
                return;
            }

            if (/^[0-9a-f]{24}$/i.test(val)) {
                setCloudFileId(val);
                try {
                    const res = await getCloudFileById(val, token as string);
                    console.log(res, "tetetetete")
                    const u = toUrlString(res);
                    if (active) setServerImageSrc(u);
                } catch {
                    if (active) setServerImageSrc(undefined);
                }
                return;
            }

            if (active) setServerImageSrc(buildAbsoluteFromRelative(val));
        })();

        return () => {
            active = false;
        };
    }, [profile?.logo, token]); // ⬅️ removed serverImageSrc from deps

    const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);

    // const hex24 = /^[0-9a-f]{24}$/i;
    // const markForDelete = (id?: string | null) => {
    //     if (id && hex24.test(id) && !pendingDeleteIds.includes(id)) {
    //         setPendingDeleteIds(prev => [...prev, id]);
    //     }
    // };

    const removeLogo = () => {
        // find the current ID (formik value > profile value > cached)
        const currentId: string | null =
            (typeof formik.values.logo === "string" ? formik.values.logo : null) ||
            (typeof profile?.logo === "string" ? profile.logo : null) ||
            cloudFileId;

        // mark cloud id for deletion (24-hex)
        markForDelete(currentId);

        // clear UI + form state
        formik.setFieldValue("company_logo", null);
        formik.setFieldValue("logo", "");
        setLogoPreview(null);
        setServerImageSrc(undefined);
        setCloudFileId(null);
        if (logoInputRef.current) logoInputRef.current.value = "";
    };



    // ====== Options & local state (UI only) ======
    const categoryOptions = useMemo(
        () => [
            { value: "printing", label: "Printing" },
            { value: "signage", label: "Signage" },
            { value: "packaging", label: "Packaging" },
            { value: "logistics", label: "Logistics" },
            { value: "software", label: "Software" },
        ],
        []
    );

    const numEmployeeOptions = [
        { value: "1-10", label: "1–10" },
        { value: "11-50", label: "11–50" },
        { value: "51-200", label: "51–200" },
        { value: "201-500", label: "201–500" },
        { value: "500+", label: "500+" },
    ];

    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const logoInputRef = useRef<HTMLInputElement | null>(null);

    // ---- Core Specialties (UI-only temp state)
    const [selectedSpecialty, setSelectedSpecialty] = useState<any>(null);
    const [specialtyInput, setSpecialtyInput] = useState("");
    const specialtyOptions = useMemo(() => [], []); // plug real options if you have them
// State for parent signage firm (for signageteammember login)
const [parentSignageId, setParentSignageId] = useState<string | null>(null);
const [parentSignageName, setParentSignageName] = useState<string | null>(null);

// prevents repeated calls (also avoids React 18 StrictMode double-fetch in dev)
const parentHierarchyFetchedRef = React.useRef(false);

// Fetch parent hierarchy for signageteammember login
const fetchParentHierarchy = React.useCallback(async () => {
  if (!isSignageTeammember || !token) return null;

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/parent-hierarchy`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) return null;

    const data = await response.json();

    const signageFirm = data.parent_chain?.find(
      (parent: any) => parent.license_type === "SIGNAGE_FIRM"
    );

    if (signageFirm) {
      const id = signageFirm.id || signageFirm._id;
      setParentSignageId(id);
      setParentSignageName(signageFirm.name);
      return id as string;
    }

    return null;
  } catch (error) {
    safeConsole.error("Error fetching parent hierarchy:", error);

    await handleApiError(
      error,
      {
        module: "removal",
        action: "Signage_Add_Removal_Request_fetch_parent_hierarchy",
        userId: userId || undefined,
        licenseType: undefined,
        userName: userName || undefined,
        url: `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        method: "GET",
      },
      token
    );

    return null;
  }
}, [isSignageTeammember, token, userId, userName]);

/** 1) Fetch parent hierarchy ONCE (only for teammember) */
React.useEffect(() => {
  if (!isSignageTeammember || !token) return;
  if (parentHierarchyFetchedRef.current) return;

  parentHierarchyFetchedRef.current = true;
  fetchParentHierarchy();
}, [isSignageTeammember, token, fetchParentHierarchy]);

/** 2) Load marketplace profiles (wait until parentSignageId is ready for teammember) */
React.useEffect(() => {
  let ignore = false;

  // if teammember login, don't load profiles until we have parentSignageId
  if (isSignageTeammember && !parentSignageId) return;
  if (!token) return;

  (async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/marketplace-profiles`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data: VendorProfile[] = await res.json();

      const parent = isSignageTeammember ? parentSignageId : userId;

      const found: VendorProfile | null = Array.isArray(data)
        ? data.find((x) => String(x.user_id) === String(parent)) ?? null
        : null;

      if (!ignore) {
        setProfile(found);
        setEditingId(found?._id ?? null);
        setIsEdit(!!found);
      }
    } catch (err) {
      console.error("Failed to load marketplace profiles:", err);
      if (!ignore) {
        setProfile(null);
        setEditingId(null);
        setIsEdit(false);
      }
    }
  })();

  return () => {
    ignore = true;
  };
}, [token, userId, isSignageTeammember, parentSignageId]);



    const handleAddSpecialty = () => {
        const list = [...(formik.values.specialties || [])];
        if (selectedSpecialty?.value) {
            if (!list.some((x: any) => x.value === selectedSpecialty.value)) list.push(selectedSpecialty);
            setSelectedSpecialty(null);
        } else if (specialtyInput.trim()) {
            const v = specialtyInput.trim();
            if (!list.some((x: any) => x.value === v)) list.push({ value: v, label: v });
            setSpecialtyInput("");
        }
        formik.setFieldValue("specialties", list);
    };

    // ---- Certifications (UI-only temp state)
    const [selectedCert, setSelectedCert] = useState<any>(null);
    const [certInput, setCertInput] = useState("");
    const certificationOptions = useMemo(() => [], []); // plug real options if you have them

    const handleAddCertification = () => {
        const list = [...(formik.values.certifications || [])];
        if (selectedCert?.value) {
            if (!list.some((x: any) => x.value === selectedCert.value)) list.push(selectedCert);
            setSelectedCert(null);
        } else if (certInput.trim()) {
            const v = certInput.trim();
            if (!list.some((x: any) => x.value === v)) list.push({ value: v, label: v });
            setCertInput("");
        }
        formik.setFieldValue("certifications", list);
    };

    const toCreatePayload = (
        v: any,
        userId: string,
        ctx?: { isEdit: boolean; profile: VendorProfile | null }
    ): CreateMarketplaceProfilePayload => {
        const toNum = (x: any) => {
            const n = Number(String(x ?? "").replace(/[^\d.-]/g, ""));
            return Number.isFinite(n) ? n : undefined;
        };

        const toList = (arr: any[]) => (arr || [])
            .map((o: any) => o?.value ?? o?.label)
            .filter(Boolean) as string[];

        const social_links = socialLinksPayloadFromValues(v.social_links);

        const termsText = String(v.terms_and_conditions ?? "").trim();
        let terms_and_conditions: CreateMarketplaceProfilePayload["terms_and_conditions"] | undefined;
        if (termsText) {
            const now = new Date().toISOString();
            const prevCreated = ctx?.profile?.terms_and_conditions?.created_at;
            const created_at =
                ctx?.isEdit && prevCreated ? prevCreated : now;
            terms_and_conditions = { text: termsText };
        }

        return {
            // --- top-level ---
            user_id: userId,
            vendor_name: v.vendor_name?.trim(),
            logo: v.logo || undefined,           // set if you have a URL
            banner: v.banner || undefined,       // set if you have a URL
            category: v.category,
            tag_line: v.tagline,                 // <-- your field is `tagline`
            description: v.description,
            about_company: v.about,              // <-- your field is `about`

            ...(social_links ? { social_links } : {}),
            ...(terms_and_conditions ? { terms_and_conditions } : {}),

            // --- contact_info (your fields live at root) ---
            contact_info: {
                website: v.website,
                email: v.email,
                phone: v.phone,
                location: {
                    label: v.label_of_address,
                    addressLine1: v.addressLine1,
                    addressLine2: v.addressLine2,
                    city: v.city,
                    state: v.state,           // or prefer `stateCode` if backend expects codes
                    country: v.country,         // or `countryCode`
                    zip: v.zipcode,
                    cityCode: v.cityCode,
                    stateCode: v.stateCode,
                    countryCode: v.countryCode,
                    latitude: toNum(v.latitude),
                    longitude: toNum(v.longitude),
                    phone: v.phone,
                },
            },

            // --- business_details (your fields live at root) ---
            business_details: {
                year_of_established: toNum(v.year_established),
                number_of_employees: toNum(v.employees),       // left undefined if not a pure number
                response_time: toNum(v.response_time),   // hours; undefined if "4–8h"
                completed_projects: toNum(v.completed_projects),
                price_model: v.pricing_model,
            },

            core_specialties: toList(v.specialties),
            certifications_compliance: toList(v.certifications),

            // you have `highlights` array; join it into a single string for the API
            why_choose_us: Array.isArray(v.highlights) && v.highlights.length
                ? v.highlights.filter(Boolean).join(" | ")
                : v.why_choose_us || "",

            verified: true,
            partner: !!v.partner,
            status: v.status || "pending",
            catalog_visibility: (v.catalog_visibility || "public") as "public" | "assigned",
            is_private: Boolean(v.is_private),
        };
    };


    const handleCreateSubmit = async (values: any, helpers: any) => {
        const user_Id = values.user_id || userId; // <- supply from your auth

        helpers.setStatus(undefined);
        helpers.setSubmitting(true);

        try {
            // let logoUrl = profile?.logo || "";

            // // Upload logo if a new file is selected
            // if (values.company_logo) {
            //     logoUrl = await uploadFile(values.company_logo);
            // }


            let imageFileId = cloudFileId || ""; // keep existing if present
            if (values.image instanceof File) {
                const uploaded = await uploadCloudFile({ file: values.image, token });
                imageFileId = uploaded.id;
            }
            const generated_id = await resolveImageId(values.image, token, imageFileId);


            // in handleCreateSubmit, after success (before navigate/reload)
            if (pendingDeleteIds.length) {
                try { await hardDeletePresignedFiles(pendingDeleteIds, token || null); } catch { }
                setPendingDeleteIds([]);
            }

            // Update values with the uploaded logo URL
            const updatedValues = {
                ...values,
                logo: generated_id
            };

            const payload = toCreatePayload(updatedValues, user_Id, {
                isEdit: false,
                profile: null,
            });

            const res = await fetch(`${process.env.REACT_APP_API_URL}/marketplace-profiles`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || `Create failed (${res.status})`);
            }

            const data = await res.json();
            // success UX (toast/snackbar/modal—your choice)
            console.log("Profile created:", data);
            helpers.setStatus({ success: true, message: "Vendor profile created successfully." });
            showSuccess('Vendor profile created successfully')
            navigate(0)
            // optional: navigate away or reset the form
            // router.push(`/vendors/${data._id}`)
        } catch (e: any) {
            console.error(e);
            helpers.setStatus({ error: e.message || "Unable to create vendor profile." });
            showError(e.message || "Unable to create vendor profile.")
        } finally {
            helpers.setSubmitting(false);
        }
    };

    // PUT /marketplace-profiles/:id  — send full payload (same shape as Create)
    const handleUpdateSubmit = async (values: any, helpers: any) => {
        // we set this in useEffect after GET; fallback to profile/vendorId if you prefer
        const id = editingId;
        if (!id) {
            helpers.setStatus({ error: "No profile selected to update." });
            return;
        }

        helpers.setSubmitting(true);
        helpers.setStatus(undefined);

        try {
            let newLogoId = (typeof values.logo === "string" ? values.logo : "") || "";
            // If a new file is staged, upload it now
            if (values.company_logo instanceof File) {
                const uploaded = await uploadCloudFile({ file: values.company_logo, token });
                const idStr = typeof uploaded === "string" ? uploaded : uploaded?.id;
                if (!idStr) throw new Error("Image upload failed");
                newLogoId = idStr;
            }
            // Update values with the uploaded logo URL
            const updatedValues = {
                ...values,
                logo: newLogoId
            };

            // reuse your existing mapper so we send the exact same structure as Create
            const payload = toCreatePayload(updatedValues, values.user_id || userId || "", {
                isEdit: true,
                profile,
            });

            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/marketplace-profiles/${id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                }
            );

            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.message || `Update failed (${res.status})`);


            // Hard-delete any previously marked images after successful update
            if (pendingDeleteIds.length) {
                try { await hardDeletePresignedFiles(pendingDeleteIds, token || null); } catch { }
                setPendingDeleteIds([]);
            }
            // toast + form status
            showSuccess("Vendor profile updated successfully");
            helpers.setStatus({ success: true, message: "Vendor profile updated successfully." });
            navigate(0)

            // optionally sync local state so UI reflects server truth
            setProfile((prev) => (prev ? { ...prev, ...data } : prev));
        } catch (e: any) {
            console.error(e);
            showError(e.message || "Unable to update vendor profile.");
            helpers.setStatus({ error: e.message || "Unable to update vendor profile." });
        } finally {
            helpers.setSubmitting(false);
        }
    };


    // one place to compute your form values from the API profile
    const initialValues = React.useMemo(() => ({
        // top-level
        user_id: userId,
        vendor_name: profile?.vendor_name || "",
        category: profile?.category || "",
        tagline: profile?.tag_line || "",
        description: profile?.description || "",
        about: profile?.about_company || "",
        logo: profile?.logo || "",
        banner: profile?.banner || "",

        // contact (you keep these at root)
        website: profile?.contact_info?.website || "",
        email: profile?.contact_info?.email || "",
        phone: profile?.contact_info?.phone || "",

        // location (Google autocomplete friendly)
        label_of_address: profile?.contact_info?.location?.label || "",
        addressLine1: profile?.contact_info?.location?.addressLine1 || "",
        addressLine2: profile?.contact_info?.location?.addressLine2 || "",
        city: profile?.contact_info?.location?.city || "",
        state: profile?.contact_info?.location?.state
            || profile?.contact_info?.location?.stateCode || "",
        country: profile?.contact_info?.location?.country
            || profile?.contact_info?.location?.countryCode || "",
        zipcode: profile?.contact_info?.location?.zip || "",
        cityCode: profile?.contact_info?.location?.cityCode || "",
        stateCode: profile?.contact_info?.location?.stateCode || "",
        countryCode: profile?.contact_info?.location?.countryCode || "",
        latitude: profile?.contact_info?.location?.latitude ?? "",
        longitude: profile?.contact_info?.location?.longitude ?? "",

        social_links: {
            facebook: profile?.social_links?.facebook || "",
            instagram: profile?.social_links?.instagram || "",
            twitter: profile?.social_links?.twitter || "",
            linkedin: profile?.social_links?.linkedin || "",
            youtube: profile?.social_links?.youtube || "",
        },

        terms_and_conditions: profile?.terms_and_conditions?.text || "",

        // business (your fields live at root)
        year_established: profile?.business_details?.year_of_established ?? "",
        employees: profile?.business_details?.number_of_employees ?? "",
        response_time: profile?.business_details?.response_time ?? "",
        completed_projects: profile?.business_details?.completed_projects ?? "",
        pricing_model: profile?.business_details?.price_model || "",

        // selects (Creatable/multi expect {value,label})
        specialties: (profile?.core_specialties ?? []).map(s => ({ value: s, label: s })),
        certifications: (profile?.certifications_compliance ?? []).map(s => ({ value: s, label: s })),

        // text area(s)
        why_choose_us: profile?.why_choose_us || "",
        // if you still use a "highlights" list UI, hydrate a single item from server string:
        highlights: profile?.why_choose_us ? [profile.why_choose_us] : [""],

        // flags
        verified: !!profile?.verified,
        partner: !!profile?.partner,
        status: profile?.status || "pending",

        // ✅ NEW: catalog visibility UI state (UI-only)
        catalog_visibility: profile?.catalog_visibility ?? "public",
        is_private: profile?.is_private === true,

        // upload helpers
        company_logo: null as File | null,
    }), [profile, userId]);


    // ====== Formik ======
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: Yup.object({
            vendor_name: Yup.string().required("Vendor name is required"),
            category: Yup.string().required("Category is required"),
            email: Yup.string().email("Invalid email").required("Email is required"),
            website: Yup.string().required("Invalid URL"),
            year_established: Yup.number()
                .typeError("Enter a valid year")
                .min(1800, "Too old")
                .max(new Date().getFullYear(), "Future years not allowed")
                .nullable(),
        }),
        onSubmit: isEdit ? handleUpdateSubmit : handleCreateSubmit

    });



    // ====== Simulate edit hydrate (UI only) ======
    // useEffect(() => {
    //     if (!isEdit) return;
    //     // TODO: fetch vendor by id and set values; below is a minimal mock:
    //     const mock = {
    //         vendor_name: "Cobb Printworks",
    //         category: "printing",
    //         tagline: "Quality, on time, every time.",
    //         description: "We offer high-volume digital and offset printing.",
    //         about:
    //             "Founded in 2004. We specialize in retail signage, packaging, and promotional materials.",
    //         website: "https://example.com",
    //         email: "hello@example.com",
    //         phone: "+1 (555) 123-4567",
    //         location: "Atlanta, GA",
    //         year_established: "2004",
    //         employees: "51-200",
    //         response_time: "4–8h, same day",
    //         completed_projects: "4k, 8k+, 10k+",
    //         pricing_model: "Per project; volume discounts",
    //         specialties: [{ value: "retail-signage", label: "Retail Signage" }],
    //         certifications: [{ value: "iso9001", label: "ISO 9001" }],
    //         highlights: ["Fast turnaround"],
    //     };
    //     formik.setValues({
    //         ...formik.initialValues,
    //         ...mock,
    //     });
    // }, [isEdit]); // eslint-disable-line

    // ====== Logo preview handling ======
    // helper (only once in file)
    const buildImageUrl = (raw?: string) => {
        if (!raw) return "";
        if (/^https?:\/\//i.test(raw)) return raw;
        const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
        const path = String(raw).replace(/\\/g, "/").replace(/^\/+/, "");
        return `${base}/${encodeURI(path)}`;
    };

    // if you use pending delete queue:
    const hex24 = /^[0-9a-f]{24}$/i;
    const markForDelete = (id?: string | null) => {
        if (id && hex24.test(id) && !pendingDeleteIds.includes(id)) {
            setPendingDeleteIds(prev => [...prev, id]);
        }
    };
    const currentLogoId = (): string | null => {
        const fv = typeof formik.values.logo === "string" ? formik.values.logo : null;
        const pv = typeof profile?.logo === "string" ? profile.logo : null;
        return fv || pv || cloudFileId || null;
    };

    // Preview-only; DO NOT upload here
    const handleLogoChange = (file?: File | null) => {
        const f = file ?? logoInputRef.current?.files?.[0] ?? null;
        if (!f) return;

        // 1) Show immediate blob preview
        const blob = URL.createObjectURL(f);
        setLogoPreview(prev => { if (prev) { try { URL.revokeObjectURL(prev); } catch { } } return blob; });

        // 2) Mark old cloud id for deletion (but we delete only AFTER successful submit)
        markForDelete(currentLogoId());

        // 3) Keep File in form state; clear the server id so submit logic knows to upload a fresh one
        formik.setFieldValue("company_logo", f);
        formik.setFieldValue("logo", ""); // signal “new image selected”
        // NOTE: do NOT change cloudFileId here; we only set it after a successful upload on submit
    };

    // const handleLogoChange = (file?: File | null) => {
    //     const f = file || logoInputRef.current?.files?.[0];
    //     if (!f) return;
    //     formik.setFieldValue("company_logo", f);
    //     const reader = new FileReader();
    //     reader.onload = (e) => setLogoPreview(e.target?.result as string);
    //     reader.readAsDataURL(f);
    // };
    // const removeLogo = () => {
    //     formik.setFieldValue("company_logo", null);
    //     formik.setFieldValue("logo", ""); // Clear the logo URL as well
    //     setLogoPreview(null);
    //     if (logoInputRef.current) logoInputRef.current.value = "";
    // };

    // ====== Helpers for react-select controlled values ======
    const categoryValue =
        categoryOptions.find((o) => o.value === formik.values.category) || null;
    const employeesValue =
        numEmployeeOptions.find((o) => o.value === formik.values.employees) || null;


    // images 

    const joinUrl = (base?: string, path?: string) => {
        if (!path) return "";
        if (!base) return path;
        if (/^https?:\/\//i.test(path)) return path;
        const b = base.replace(/\/+$/, "");
        const p = path.replace(/^\/+/, "");
        return `${b}/${p}`;
    };

    const displaySrc: string | undefined =
        logoPreview ??                                           // show picked file preview first
        serverImageSrc ??                                        // existing server image (presigned or absolute)
        (typeof formik.values.logo === "string" && formik.values.logo
            ? (/^\/?uploads\//i.test(formik.values.logo)
                ? joinUrl(process.env.REACT_APP_BASE_URL, formik.values.logo)
                : buildImageUrl(formik.values.logo))
            : undefined);


    // ====== UI ======
    return (
        <div className="container d-lg-flex justify-content-lg-center py-3">
            <div className="row col-lg-9">
                {/* HEADER / PROGRESS */}
                <div className="mb-4">
                    <div className="d-flex align-items-start gap-3">
                        <div className="br-8 p-3 vendor-icon-chip vendor-icon-chip--outlined">
                            <Building2 size={32} />
                        </div>

                        <div>
                            <h4 className="mb-1 fw-bold fs-24">
                                {isEdit ? "Edit Vendor Profile" : "Add New Vendor Profile"}
                            </h4>
                            <div className="text-muted fs-17">
                                Create a comprehensive vendor profile for your marketplace
                            </div>
                        </div>
                    </div>

                    <div className="card mt-4 border-2 shadow-lg br-8">
                        <div className="card-body d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-3">
                                <div className="rounded-circle vendor-icon-chip" style={{ padding: 14 }}>
                                    <Building2 size={26} />
                                </div>

                                <div>
                                    <h4 className="fs-18">Vendor Information</h4>
                                    <div className="small text-muted fs-16">
                                        Complete all sections to create a professional vendor profile
                                    </div>
                                </div>
                            </div>
                            <div className="align-items-center d-flex flex-column">
                                <span className="fs-20" style={{ color: "#155dfc" }}>✓</span> <span className="text-muted">Form Status</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* FORM */}
                <form onSubmit={formik.handleSubmit} className="card border-2 shadow-lg p-4 br-8 mb-5">
                    {/* BASIC INFORMATION */}
                    <div className="border-0 mb-3">
                        <div className="">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <Building2 size={20} className="text-muted" />
                                <h5 className="mb-0 fs-17">Basic Information</h5>
                            </div>

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <UIInputFF
                                        formik={formik as any}
                                        name="vendor_name"
                                        label="Vendor Name "
                                        placeholder="Enter vendor name"
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fs-13">Category <span className="text-danger">*</span></label>
                                    <Select
                                        classNamePrefix="react-select rs"
                                        placeholder="Select category"
                                        options={categoryOptions}
                                        value={categoryValue}
                                        onChange={(opt: any) =>
                                            formik.setFieldValue("category", opt?.value || "")
                                        }
                                        components={{ DropdownIndicator }}
                                        styles={customSelectStyles}
                                    />
                                    {formik.touched.category && formik.errors.category && (
                                        <div className="text-danger small mt-1">
                                            {formik.errors.category as any}
                                        </div>
                                    )}
                                </div>

                                <div className="col-12">
                                    <UIInputFF
                                        formik={formik as any}
                                        name="tagline"
                                        label="Tagline "
                                        placeholder="Brief compelling tagline"
                                        required
                                    />
                                </div>

                                <div className="col-12">
                                    <label className="form-label fs-13">Description <span className="text-danger">*</span></label>
                                    <textarea
                                        className="form-control br-8"
                                        rows={4}
                                        placeholder="Detailed description of services and capabilities"
                                        style={{ resize: "none" }}
                                        {...formik.getFieldProps("description")}
                                    />
                                </div>

                                <div className="col-12">
                                    <label className="form-label fs-13">About the Company <span className="text-danger">*</span></label>
                                    <textarea
                                        className="form-control br-8"
                                        rows={4}
                                        placeholder="Company background, history, and mission"
                                        style={{ resize: "none" }}
                                        {...formik.getFieldProps("about")}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CONTACT INFORMATION */}
                    <div className="border-0 mb-3">
                        <div className="">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                {/* <i className="ri-lock-2-line text-muted" /> */}
                                <h5 className="mb-0 fs-17">Contact Information</h5>
                            </div>

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <UIInputFF
                                        formik={formik as any}
                                        name="website"
                                        label="Website "
                                        placeholder="www.example.com"
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <UIInputFF
                                        formik={formik as any}
                                        name="email"
                                        label="Email "
                                        placeholder="contact@example.com"
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <UIInputFF
                                        formik={formik as any}
                                        name="phone"
                                        label="Phone "
                                        placeholder="+1 (555) 123-4567"
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">
                                        location <span className="text-danger">*</span>
                                    </label>
                                    <AddressAutocomplete
                                        id="label_of_address"
                                        name="label_of_address"
                                        value={formik.values.label_of_address || formik.values.addressLine1}
                                        onChange={(v) => {
                                            formik.setFieldValue("label_of_address", v);
                                            formik.setFieldValue("addressLine1", v);
                                        }}
                                        onSelect={(picked) => {
                                            const fullAddress = `${picked.addressLine1}, ${picked.city}, ${picked.state}, ${picked.zipcode}`;
                                            formik.setFieldValue("label_of_address", fullAddress);
                                            formik.setFieldValue("addressLine1", picked.addressLine1);
                                            formik.setFieldValue("city", picked.city);
                                            formik.setFieldValue("cityCode", picked.cityCode);
                                            formik.setFieldValue("state", picked.state);
                                            formik.setFieldValue("stateCode", picked.stateCode);
                                            formik.setFieldValue("zipcode", picked.zipcode);
                                            formik.setFieldValue("country", picked.country);
                                            formik.setFieldValue("countryCode", picked.countryCode);
                                            formik.setFieldValue("latitude", picked.latitude);
                                            formik.setFieldValue("longitude", picked.longitude);
                                        }}
                                        className={`form-control ${formik.touched.label_of_address && formik.errors.label_of_address ? "is-invalid" : ""}`}
                                        placeholder="Enter location address"
                                        error={formik.touched.label_of_address && formik.errors.label_of_address ? (formik.errors.label_of_address as string) : undefined}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BUSINESS DETAILS */}
                    <div className="border-0 mb-3">
                        <div className="">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                {/* <i className="ri-lock-2-line text-muted" /> */}
                                <h5 className="mb-0 fs-17">Business Details</h5>
                            </div>

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <UIInputFF
                                        formik={formik as any}
                                        name="year_established"
                                        label="Year Established"
                                        placeholder="2005"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fs-13">Number of Employees</label>
                                    <Select
                                        classNamePrefix="react-select rs"
                                        placeholder="e.g., 1-10, 50-200, 100+"
                                        options={numEmployeeOptions}
                                        value={employeesValue}
                                        onChange={(opt: any) =>
                                            formik.setFieldValue("employees", opt?.value || "")
                                        }
                                        components={{ DropdownIndicator }}
                                        styles={customSelectStyles}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <UIInputFF
                                        formik={formik as any}
                                        name="response_time"
                                        label="Response Time"
                                        placeholder="e.g., 4–8h, same day"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <UIInputFF
                                        formik={formik as any}
                                        name="completed_projects"
                                        label="Completed Projects"
                                        placeholder="e.g., 4k, 8k+, 10k+"
                                    />
                                </div>
                                <div className="col-12">
                                    <UIInputFF
                                        formik={formik as any}
                                        name="pricing_model"
                                        label="Pricing Model"
                                        placeholder="e.g., Per project, Volume discounts, Monthly service"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CORE SPECIALTIES */}
                    <div className="border-0 mb-3">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <h5 className="mb-0 fs-17">Core Specialties</h5>
                        </div>

                        <div className="row g-3 align-items-end">
                            <div className="col-lg-5 col-md-6">
                                <label className="form-label fs-13">Select or add specialty</label>
                                <Select
                                    classNamePrefix="react-select rs"
                                    placeholder="Select specialty"
                                    options={specialtyOptions}
                                    value={selectedSpecialty}
                                    onChange={(opt) => setSelectedSpecialty(opt)}
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                />
                            </div>

                            <div className="col-lg-5 col-md-6">
                                <label className="form-label fs-13">Or type custom specialty</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Or type custom specialty"
                                    value={specialtyInput}
                                    onChange={(e) => setSpecialtyInput(e.target.value)}
                                />
                            </div>

                            <div className="col-lg-1 col-auto d-grid">
                                <button
                                    type="button"
                                    className="btn btn-primary table-addbutton"
                                    onClick={handleAddSpecialty}
                                    title="Add specialty"
                                >
                                    <i className="ri-add-line" />
                                </button>
                            </div>
                        </div>

                        {/* Display selected specialties */}
                        {formik.values.specialties && formik.values.specialties.length > 0 && (
                            <div className="mt-3">
                                <label className="form-label fs-13 mb-2">Selected Specialties:</label>
                                <div className="row g-2">
                                    {formik.values.specialties.map((specialty: any, index: number) => (
                                        <div className="col-auto br-8" key={index}>
                                            <div className="d-flex align-items-center gap-2 p-2 rounded border" style={{ backgroundColor: '#e8f0ff' }}>
                                                <span className="fs-14" style={{ color: '#155dfc' }}>{specialty.label || specialty.value}</span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm p-0"
                                                    style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#155dfc', border: '1px solid #155dfc' }}
                                                    onClick={() => {
                                                        const updatedSpecialties = formik.values.specialties.filter((_: any, i: number) => i !== index);
                                                        formik.setFieldValue("specialties", updatedSpecialties);
                                                    }}
                                                    title="Remove specialty"
                                                >
                                                    <i className="ri-close-line fs-12" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>


                    {/* CERTIFICATIONS */}
                    <div className="border-0 mb-3">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <h5 className="mb-0 fs-17">Certifications &amp; Compliance</h5>
                        </div>

                        <div className="row g-3 align-items-end">
                            <div className="col-lg-5 col-md-6">
                                <label className="form-label fs-13">Select or add certification</label>
                                <Select
                                    classNamePrefix="react-select rs"
                                    placeholder="Select certification"
                                    options={certificationOptions}
                                    value={selectedCert}
                                    onChange={(opt) => setSelectedCert(opt)}
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                />
                            </div>

                            <div className="col-lg-5 col-md-6">
                                <label className="form-label fs-13">Or type custom certification</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Or type custom certification"
                                    value={certInput}
                                    onChange={(e) => setCertInput(e.target.value)}
                                />
                            </div>

                            <div className="col-lg-1 col-auto d-grid">
                                <button
                                    type="button"
                                    className="btn btn-primary table-addbutton"
                                    onClick={handleAddCertification}
                                    title="Add certification"
                                >
                                    <i className="ri-add-line" />
                                </button>
                            </div>
                        </div>

                        {/* Display selected certifications */}
                        {formik.values.certifications && formik.values.certifications.length > 0 && (
                            <div className="mt-3">
                                <label className="form-label fs-13 mb-2">Selected Certifications:</label>
                                <div className="row g-2">
                                    {formik.values.certifications.map((certification: any, index: number) => (
                                        <div className="col-auto" key={index}>
                                            <div className="d-flex align-items-center gap-2 p-2 rounded border" style={{ backgroundColor: '#e8f0ff' }}>
                                                <span className="fs-14" style={{ color: '#155dfc' }}>{certification.label || certification.value}</span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm p-0"
                                                    style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#155dfc', border: '1px solid #155dfc' }}
                                                    onClick={() => {
                                                        const updatedCertifications = formik.values.certifications.filter((_: any, i: number) => i !== index);
                                                        formik.setFieldValue("certifications", updatedCertifications);
                                                    }}
                                                    title="Remove certification"
                                                >
                                                    <i className="ri-close-line fs-12" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>


                    {/* HIGHLIGHTS */}
                    <div className="border-0 mb-3">
                        <div className="">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                {/* <i className="ri-lock-2-line text-muted" /> */}
                                <h5 className="mb-0 fs-17">Why Choose Us (Sales Highlights)</h5>
                            </div>

                            {formik.values.highlights.map((h, i) => (
                                <div className="row g-3 align-items-center mb-2" key={i}>
                                    <div className="col-lg-10">
                                        <UIInputFF
                                            formik={formik as any}
                                            name={`highlights[${i}]`}
                                            label={i === 0 ? "Enter a key selling point" : undefined}
                                            placeholder="e.g., 24h turnaround on urgent jobs"
                                        />
                                    </div>
                                    <div className="col-lg-1 d-grid">
                                        <button
                                            type="button"
                                            className={`btn mt-1 ${i === 0 ? "d-none" : "btn-outline-danger"}`}
                                            onClick={() =>
                                                i === 0
                                                    ? null
                                                    : formik.setFieldValue(
                                                        "highlights",
                                                        formik.values.highlights.filter((_, idx) => idx !== i)
                                                    )
                                            }
                                        >
                                            <i className="ri-close-line" />
                                        </button>
                                    </div>
                                </div>
                            ))}

                            <button
                                type="button"
                                className="br-8 btn btn-upload px-4 py-3"
                                onClick={() => formik.setFieldValue("highlights", [...formik.values.highlights, ""])}
                            >
                                + Add Another Highlight
                            </button>
                        </div>
                    </div>

                    {/* CATALOG VISIBILITY */}
                    <div className="border-0 border-top mb-4 mt-3 pt-4">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <i className="ri-eye-line text-muted fs-20" />
                            <h5 className="mb-0 fs-17">Catalog Visibility</h5>
                        </div>

                        <p className="text-muted mb-4">
                            Choose who can view your products and services in the marketplace.
                        </p>


                        <div className="row g-3">
                            <div className="col-12">
                                <div className="d-flex flex-column gap-3">

                                    {/* Option 1: Assigned only */}
                                    <label
                                        className={`visibility-card p-3 br-8 d-flex align-items-start gap-3 ${formik.values.catalog_visibility === "assigned" ? "is-active" : ""
                                            }`}
                                        role="button"
                                    >
                                        <input
                                            type="radio"
                                            className="form-check-input mt-1"
                                            name="catalog_visibility"
                                            value="assigned"
                                            checked={formik.values.catalog_visibility === "assigned"}
                                            onChange={() => formik.setFieldValue("catalog_visibility", "assigned")}
                                        />
                                        <span className="">
                                            <Users size={20} className="text-muted" />
                                        </span>
                                        <div>
                                            <div className="fs-16 fw-medium mb-2">Show only to assigned Brokerages/Agents/Groups</div>
                                            <div className="text-muted small">
                                                Your products will only be visible to specific brokerages, agents, or groups that you’ve
                                                partnered with. This provides exclusive access to your services.
                                            </div>
                                        </div>
                                    </label>

                                    {/* Option 2: Public */}
                                    <label
                                        className={`visibility-card p-3 br-8 d-flex align-items-start gap-3 ${formik.values.catalog_visibility === "public" ? "is-active" : ""
                                            }`}
                                        role="button"
                                    >
                                        <input
                                            type="radio"
                                            className="form-check-input mt-1"
                                            name="catalog_visibility"
                                            value="public"
                                            checked={formik.values.catalog_visibility === "public"}
                                            onChange={() => formik.setFieldValue("catalog_visibility", "public")}
                                        />
                                        <span className="">
                                            <Globe size={20} className="text-muted" />
                                        </span>
                                        <div>
                                            <div className="fs-16 fw-medium mb-2">Show public products to everyone else</div>
                                            <div className="text-muted small">
                                                Your products will be visible to all users on the platform. This maximizes your reach and
                                                allows any real estate professional to discover your services.
                                            </div>
                                        </div>
                                    </label>

                                </div>
                            </div>

                            {/* Note callout */}
                            <div className="col-12">
                                <div className="note-callout p-3 br-8">
                                    <div className="d-flex gap-2">
                                        <i className="ri-information-line" />
                                        <div className="small">
                                            <strong className="fs-13">Note about visibility settings:</strong>
                                            <div className="mt-2 fs-12">
                                                You can change these settings later from your vendor dashboard. Visibility settings only
                                                affect product discovery — contracted partners will always have access to your full catalog.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BRANDING — social links + terms (above primary actions) */}
                    <div className="border-0 border-top mb-4 mt-3 pt-4">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <i className="ri-palette-line text-muted fs-20" aria-hidden />
                            <h5 className="mb-0 fs-17">Branding</h5>
                        </div>
                        <p className="text-muted small mb-4">
                            Social profiles and terms shown with your marketplace vendor presence.
                        </p>

                         {/* COMPANY LOGO */}
                    <div className="border-0 mb-3">
                        <div className="">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <Building2 size={20} className="text-muted" />
                                <h5 className="mb-0 fs-17">Company Logo</h5>
                            </div>

                            <div className="row g-3">
                                <div className="col-lg-12">
                                    <div className="align-items-lg-center d-flex gap-3">
                                        <div className="br-8 p-2 vendor-icon-chip vendor-icon-chip--outlined" style={{ width: 84, height: 84 }}>
                                            {displaySrc ? (
                                                <img
                                                    src={displaySrc}
                                                    alt="Preview"
                                                    style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 10 }}
                                                />
                                            ) : (
                                                <span className="text-muted" style={{ fontSize: 35 }}>🏢</span>
                                            )}
                                            {/* {logoPreview ? (
                                                <img
                                                    src={logoPreview}
                                                    alt="logo"
                                                    style={{ width: 78, height: 78, objectFit: "contain" }}
                                                />
                                            ) : profile?.logo ? (
                                                <img
                                                    src={`${process.env.REACT_APP_BASE_URL}${profile.logo}`}
                                                    alt="logo"
                                                    style={{ width: 78, height: 78, objectFit: "contain" }}
                                                />
                                            ) : (
                                                // <i className="ri-image-2-line fs-3 text-muted" />
                                                <span className="text-muted" style={{ fontSize: 35 }}>🏢</span>
                                            )} */}
                                        </div>
                                        <div>
                                            <div className="fw-bold fs-14 mb-2">Upload Company Logo</div>
                                            <div className="text-muted small fs-14">
                                                Upload a high-quality logo image. Recommended size: 200x200px. Max 5MB.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-12">
                                    <div
                                        className="upload-drop mt-3"
                                        role="button"
                                        aria-label="Upload company logo"
                                        tabIndex={0}
                                        onClick={(e) => {
                                            if ((e.target as HTMLElement).tagName.toLowerCase() !== "button") {
                                                logoInputRef.current?.click();
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                logoInputRef.current?.click();
                                            }
                                        }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            const file = e.dataTransfer.files?.[0] || null;
                                            if (file) void handleLogoChange(file);
                                        }}
                                    >
                                        <i className="ri-upload-2-line upload-drop__icon" aria-hidden="true" />
                                        <div className="upload-drop__title">Drag &amp; drop your logo here</div>
                                        <div className="upload-drop__sub">or click to browse files</div>

                                        <button
                                            type="button"
                                            className="btn btn-upload mt-2"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                logoInputRef.current?.click();
                                            }}
                                        >
                                            Choose File
                                        </button>

                                        <input
                                            ref={logoInputRef}
                                            type="file"
                                            accept="image/*"
                                            className="d-none"
                                            onChange={(e) => {
                                                const f = e.currentTarget.files?.[0] ?? null;
                                                void handleLogoChange(f); // ⬅️ calls uploadCloudFile/getCloudFileById/resolveImageId
                                            }}
                                        />
                                        {(logoPreview || profile?.logo) && (
                                            <div className="mt-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeLogo();
                                                    }}
                                                >
                                                    Remove Logo
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Drop zone */}
                                    {/* <div
                                        className="upload-drop mt-3"
                                        role="button"
                                        aria-label="Upload company logo"
                                        // onClick={() => logoInputRef.current?.click()}
                                        // onDragOver={(e) => e.preventDefault()}
                                        // onDrop={(e) => {
                                        //     e.preventDefault();
                                        //     const file = e.dataTransfer.files?.[0];
                                        //     if (file) handleLogoChange(file);
                                        // }}
                                        tabIndex={0}
                                        onClick={(e) => {
                                            // avoid double-trigger if the inner button is clicked
                                            if ((e.target as HTMLElement).tagName.toLowerCase() !== "button") {
                                                logoInputRef.current?.click();
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                logoInputRef.current?.click();
                                            }
                                        }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            const file = e.dataTransfer.files?.[0] || null;
                                            if (file) handleLogoChange(file); // your function sets preview + formik file field
                                        }}
                                    >
                                        <i className="ri-upload-2-line upload-drop__icon" aria-hidden="true" />
                                        <div className="upload-drop__title">Drag &amp; drop your logo here</div>
                                        <div className="upload-drop__sub">or click to browse files</div>

                                        <button
                                            type="button"
                                            className="btn btn-upload mt-2"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                logoInputRef.current?.click();
                                            }}
                                        >
                                            Choose File
                                        </button>

                                        <input
                                            ref={logoInputRef}
                                            type="file"
                                            accept="image/*"
                                            className="d-none"
                                            // onChange={() => handleLogoChange()}
                                            onChange={(e) => {
                                                const f = e.currentTarget.files?.[0] ?? null;
                                                handleLogoChange(f);          // DO NOT set formik 'logo' to File; let your handler set 'company_logo' (File) + preview
                                                // If you really need to clear the input after selection:
                                                // e.currentTarget.value = "";
                                            }}
                                        />

                                        {(logoPreview || profile?.logo) && (
                                            <div className="mt-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeLogo();
                                                    }}
                                                >
                                                    Remove Logo
                                                </button>
                                            </div>
                                        )}
                                    </div> */}

                                    {/* helper panel under the dropzone */}
                                    <div className="upload-hint small">
                                        <strong>Supported formats:</strong> PNG, JPG, JPEG, GIF, SVG<br />
                                        <strong>Recommended:</strong> Square images (1:1 aspect ratio) for best results
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                        <div className="d-flex align-items-center gap-2 mb-3">
                            <i className="ri-share-line text-muted fs-20" aria-hidden />
                            <h6 className="mb-0 fs-16 fw-semibold">Social links</h6>
                        </div>
                        <p className="text-muted small mb-3">
                            Optional links saved on your vendor profile (aligned with invoice template social URLs).
                        </p>
                        <div className="row g-3 mb-4">
                            <div className="col-12 col-lg-6">
                                <UIInputFF
                                    formik={formik as any}
                                    name="social_links.facebook"
                                    label="Facebook"
                                    placeholder="https://facebook.com/yourprofile"
                                />
                            </div>
                            <div className="col-12 col-lg-6">
                                <UIInputFF
                                    formik={formik as any}
                                    name="social_links.instagram"
                                    label="Instagram"
                                    placeholder="https://instagram.com/yourprofile"
                                />
                            </div>
                            <div className="col-12 col-lg-6">
                                <UIInputFF
                                    formik={formik as any}
                                    name="social_links.twitter"
                                    label="Twitter / X"
                                    placeholder="https://x.com/yourprofile"
                                />
                            </div>
                            <div className="col-12 col-lg-6">
                                <UIInputFF
                                    formik={formik as any}
                                    name="social_links.linkedin"
                                    label="LinkedIn"
                                    placeholder="https://linkedin.com/in/yourprofile"
                                />
                            </div>
                            <div className="col-12 col-lg-6">
                                <UIInputFF
                                    formik={formik as any}
                                    name="social_links.youtube"
                                    label="YouTube"
                                    placeholder="https://youtube.com/@yourchannel"
                                />
                            </div>
                        </div>

                        <div
                            className="d-flex justify-content-between align-items-start gap-3 p-3 mb-4"
                            style={{
                                backgroundColor: "#F9FAFB",
                                borderRadius: 12,
                                border: "1px solid #E5E7EB",
                            }}
                        >
                            <div>
                                <label htmlFor="is_private" className="form-label mb-1 fw-semibold">
                                    Hide vendor profile from Brokerages and Agents
                                </label>
                                <p className="text-muted small mb-0">
                                    When this is on, brokerage and agent logins will not see this vendor profile in the marketplace.
                                    Leave it off to keep the profile visible.
                                </p>
                            </div>
                            <div className="form-check form-switch m-0 flex-shrink-0">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="is_private"
                                    checked={Boolean(formik.values.is_private)}
                                    onChange={(e) => formik.setFieldValue("is_private", e.target.checked)}
                                    style={{ width: 44, height: 22, cursor: "pointer" }}
                                />
                            </div>
                        </div>

                        <h6 className="fs-16 fw-semibold mb-2">Terms and Conditions</h6>
                        <p className="text-muted small mb-2">
                            Enter the policy text you want stored on your vendor profile. Timestamps are set automatically when you create or update the profile.
                        </p>
                        <UIInputFF
                            formik={formik as any}
                            name="terms_and_conditions"
                            label="Terms and conditions"
                            placeholder="Enter your terms and conditions…"
                            asTextarea
                            rows={6}
                        />
                        {isEdit &&
                        (profile?.terms_and_conditions?.created_at ||
                            profile?.terms_and_conditions?.updated_at) ? (
                            <p className="text-muted small mt-2 mb-0">
                                {profile?.terms_and_conditions?.created_at ? (
                                    <span className="me-3">
                                        Saved first:{" "}
                                        {new Date(
                                            profile.terms_and_conditions.created_at
                                        ).toLocaleString()}
                                    </span>
                                ) : null}
                                {profile?.terms_and_conditions?.updated_at ? (
                                    <span>
                                        Last updated:{" "}
                                        {new Date(
                                            profile.terms_and_conditions.updated_at
                                        ).toLocaleString()}
                                    </span>
                                ) : null}
                            </p>
                        ) : null}
                    </div>

                    {/* FOOTER ACTIONS */}
                    <div className="d-flex justify-content-end align-items-center gap-3">
                        <button
                            type="button"
                            className="btn back-btn surface-btn fw-bold fs-16"
                            onClick={() => formik.resetForm()}
                        >
                            Reset Form
                        </button>
                        {/* <Link to={`/signage/vendors`} className="btn cancel-btn surface-btn fw-bold fs-16" > Vendors </Link> */}
                        <button type="submit" className="btn btn-primary save-btn surface-btn fw-bold fs-16 me-3">
                            {isEdit ? "Update Vendor Profile" : "Create Vendor Profile"}
                        </button>
                    </div>
                </form>

                {/* local styles to match reference closely */}
                <style>{`
                    .react-select__control { border-radius: 12px; min-height: 44px; }
                    .react-select__value-container { padding: 6px 10px; }
                    .br-8 { border-radius: 12px !important; }
                    .btn-add-split{
                      background:#f3f4f6; color:#4b5563; border:1px solid #d1d5db; font-weight:600;
                      transition: background-color .12s ease, color .12s ease, border-color .12s ease;
                    }
                    .btn-add-split:hover{ background:#fff; color:#000; border-color:#000; }
                    .vendor-icon-chip{
                      background:#e8f0ff !important;
                      color:#155dfc !important;
                      line-height:1;
                      display:inline-flex; align-items:center; justify-content:center;
                    }
                    .vendor-icon-chip i{ color:inherit; }

                    /* header chip border */
                    .vendor-icon-chip--outlined{
                      border:2px solid #dbeafe;   /* requested border color */
                    }

                    /* Dropzone — match the reference */
                    .upload-drop{
                      border:2px dashed #e5e7eb !important;
                      background:#fff;
                      border-radius:12px;
                      min-height:180px;
                      padding:28px 16px;
                      display:flex;
                      flex-direction:column;
                      align-items:center;
                      justify-content:center;
                      gap:6px;
                      text-align:center;
                      cursor:pointer;
                      transition:border-color .15s ease, background-color .15s ease, box-shadow .15s ease;
                    }
                    .upload-drop:hover{
                      background:#f8fafc;
                      border-color:#99a1af !important;
                    }

                    /* Icon + text */
                    .upload-drop__icon{ font-size:28px; color:#97a3b5; margin-bottom:2px; }
                    .upload-drop__title{ font-weight:600; color:#374151; }
                    .upload-drop__sub{ color:#6b7280; }

                    /* Helper block under the dropzone */
                    .upload-hint{
                      margin-top:12px;
                      background:#f8fafc;
                      border-radius:12px;
                      padding:12px 16px;
                      color:#6b7280;
                    }
                    .upload-hint strong{ color:#374151; font-weight:600; }

                    /* Choose File button */
                        .btn-upload{
                          color:#155dfc;                 /* text */
                          background:#fff;               /* bg */
                          border:1px solid #8ec5ff;      /* border */
                          font-weight:600;
                          border-radius:10px;
                          padding:.5rem 1rem;
                          transition:background-color .15s ease, color .15s ease, border-color .15s ease, transform .08s ease;
                        }
                        .btn-upload:hover,
                        .btn-upload:focus{
                          color:#000;                    /* hover text */
                          background:#eff6ff;            /* hover bg */
                          border-color:#51a2ff;          /* hover border */
                        }
                        .btn-upload:active{
                          transform:scale(.98);
                          box-shadow:none;
                        }
                        /* Catalog Visibility */
                        .visibility-card{
                          background:#fff;
                          border:4px solid #e5e7eb;
                          transition:border-color .12s ease, background-color .12s ease, box-shadow .12s ease;
                          border-radius:12px; /* keeps the soft round */
                        }
                        .visibility-card:hover{
                          border-color:#99a1af !important;          /* requested hover border */
                          background:#fbf9fa;            /* requested hover bg */
                        }
                        .visibility-card.is-active{
                          border-color:#2b7fff !important;
                          box-shadow:0 0 0 4px rgba(43,127,255,.08);
                          background:#f8fbff;
                        }
                        .visibility-card .form-check-input{ width:18px; height:18px; }

                        /* Note callout (unchanged) */
                        .note-callout{
                          background:#eff6ff;
                          border:1px solid #bedbff;
                          color:#193cb8;
                        }
                        .note-callout i{ color:#193cb8; }


                `}</style>
            </div>
        </div>
    );
};

export default MarketplaceVendorProfile;
